const MAHAWA_GLASS_CONFIG = {
  shell: {
    blurAmount: 0.34,
    refraction: 0.4,
    chromAberration: 0.007,
    edgeHighlight: 0.32,
    specular: 0.3,
    fresnel: 0.72,
    distortion: 0.009,
    cornerRadius: 28,
    zRadius: 18,
    opacity: 0.58,
    saturation: 0.08,
    tintStrength: 0.05,
    brightness: 0.12,
    shadowOpacity: 0.12,
    shadowSpread: 9,
    shadowOffsetY: 3,
    floating: false,
    button: false,
  },
  indicator: {
    blurAmount: 0.32,
    refraction: 0.54,
    chromAberration: 0.014,
    edgeHighlight: 0.46,
    specular: 0.42,
    fresnel: 0.82,
    distortion: 0.013,
    cornerRadius: 22,
    zRadius: 16,
    opacity: 0.64,
    saturation: 0.16,
    tintStrength: 0.34,
    brightness: 0.1,
    shadowOpacity: 0.18,
    shadowSpread: 8,
    shadowOffsetY: 3,
    floating: false,
    button: false,
  },
};

const hasCompleteCartPayload = (cart) =>
  cart &&
  typeof cart === 'object' &&
  typeof cart.item_count !== 'undefined' &&
  Array.isArray(cart.items);

const getLowCapabilityReason = () => {
  if (!window.WebGLRenderingContext) return 'no-webgl';
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 'reduced-motion';

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (connection && connection.saveData) return 'save-data';
  if (Number(navigator.deviceMemory) > 0 && Number(navigator.deviceMemory) <= 4) return 'low-memory';
  if (Number(navigator.hardwareConcurrency) > 0 && Number(navigator.hardwareConcurrency) <= 4) return 'low-cpu';

  return '';
};

if (!customElements.get('ma-hawa-mobile-navigation')) {
  customElements.define(
    'ma-hawa-mobile-navigation',
    class MaHawaMobileNavigation extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.initialize();
      }

      disconnectedCallback() {
        this.nav?.removeEventListener('pointerdown', this.handleNavIntent);
        this.nav?.removeEventListener('click', this.handleNavIntent);
        this.indicator?.removeEventListener('transitionend', this.handleIndicatorSettled);
        window.removeEventListener('pagehide', this.handlePageHide);
        if (this.unsubscribeCart) this.unsubscribeCart();
        if (this.glassInstance) this.glassInstance.destroy();
        this.glassInstance = null;
      }

      initialize() {
        this.root = this.querySelector('[data-ma-hawa-glass-root]');
        this.nav = this.querySelector('.ma-hawa-mobile-nav');
        this.indicator = this.querySelector('[data-ma-hawa-glass-indicator]');
        this.count = this.querySelector('[data-bottom-cart-count]');
        this.viewCartLabel = this.dataset.viewCartLabel || '';
        this.handleNavIntent = (event) => this.onNavIntent(event);
        this.handleIndicatorSettled = (event) => this.onIndicatorSettled(event);
        this.handlePageHide = () => {
          if (this.glassInstance) this.glassInstance.destroy();
          this.glassInstance = null;
        };

        this.nav?.addEventListener('pointerdown', this.handleNavIntent);
        this.nav?.addEventListener('click', this.handleNavIntent);
        this.indicator?.addEventListener('transitionend', this.handleIndicatorSettled);
        window.addEventListener('pagehide', this.handlePageHide, { once: true });

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (hasCompleteCartPayload(event?.cartData)) {
              this.update(event.cartData);
            } else {
              this.refreshCart();
            }
          });
        }

        this.refreshCart();
        this.scrollToCheckoutFromHash();

        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            if (this.root) this.root.dataset.ready = 'true';
          });
        });

        this.initializeGlass();
      }

      setActiveIndex(value) {
        const index = Math.max(0, Math.min(3, Number.parseInt(value, 10) || 0));
        if (this.root) this.root.dataset.activeIndex = String(index);
      }

      onNavIntent(event) {
        if (event.type === 'pointerdown' && event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const item = event.target.closest('[data-nav-index]');
        if (!item || !this.nav?.contains(item)) return;
        this.setActiveIndex(item.dataset.navIndex);
      }

      onIndicatorSettled(event) {
        if (event.propertyName !== 'transform' || !this.glassInstance || !this.indicator) return;
        this.glassInstance.markChanged(this.indicator);
      }

      async initializeGlass() {
        if (!this.root) return;

        const fallbackReason = getLowCapabilityReason();
        if (fallbackReason) {
          this.root.dataset.glassMode = 'fallback';
          this.root.dataset.glassFallback = fallbackReason;
          return;
        }

        const shell = this.querySelector('[data-ma-hawa-glass-shell]');
        const indicator = this.indicator;
        const moduleUrl = this.dataset.glassModule;
        if (!shell || !indicator || !moduleUrl || shell.parentElement !== this.root || indicator.parentElement !== this.root) {
          this.root.dataset.glassMode = 'fallback';
          this.root.dataset.glassFallback = 'invalid-structure';
          return;
        }

        shell.dataset.config = JSON.stringify(MAHAWA_GLASS_CONFIG.shell);
        indicator.dataset.config = JSON.stringify(MAHAWA_GLASS_CONFIG.indicator);

        try {
          const { LiquidGlass } = await import(moduleUrl);
          const instance = await LiquidGlass.init({
            root: this.root,
            glassElements: [shell, indicator],
          });

          if (!this.isConnected) {
            instance.destroy();
            return;
          }

          this.glassInstance = instance;
          this.root.dataset.glassMode = 'webgl';
          this.root.removeAttribute('data-glass-fallback');
        } catch (error) {
          this.root.dataset.glassMode = 'fallback';
          this.root.dataset.glassFallback = 'initialization-failed';
        }
      }

      async refreshCart() {
        if (this.cartRequest) return this.cartRequest;

        this.cartRequest = (async () => {
          try {
            const response = await fetch(`${window.routes.cart_url}.js`, {
              headers: { Accept: 'application/json' },
            });
            if (!response.ok) return;
            this.update(await response.json());
          } catch (error) {
            return;
          } finally {
            this.cartRequest = null;
          }
        })();

        return this.cartRequest;
      }

      update(cart) {
        const itemCount = Number(cart.item_count) || 0;
        this.updateCount(itemCount);

        this.getProgressContexts().forEach((context) => {
          const qualifyingCount = (cart.items || []).reduce((total, item) => {
            if (!context.productIds.has(String(item.product_id))) return total;
            return total + (Number(item.quantity) || 0);
          }, 0);
          const isReady = itemCount > 0 && qualifyingCount >= context.minimumOrder;

          context.progressBar.classList.toggle('is-checkout-ready', isReady);
          context.viewCartAction.hidden = !isReady;
        });
      }

      getProgressContexts() {
        return Array.from(document.querySelectorAll('ma-hawa-shop, ma-hawa-product-cart-progress'))
          .map((host) => {
            const progressBar = host.querySelector('[data-cart-progress], [data-product-cart-progress]');
            if (!progressBar) return null;

            let viewCartAction = progressBar.querySelector('[data-progress-checkout]');
            if (!viewCartAction) {
              viewCartAction = document.createElement('a');
              viewCartAction.className = 'ma-hawa-progress-checkout';
              viewCartAction.dataset.progressCheckout = '';
              viewCartAction.href = window.routes.cart_url;
              viewCartAction.textContent = this.viewCartLabel;
              viewCartAction.hidden = true;
              progressBar.appendChild(viewCartAction);
            }

            return {
              progressBar,
              viewCartAction,
              minimumOrder: Number(host.dataset.minimumOrder) || 5,
              productIds: new Set(
                (host.dataset.minimumProductIds || '')
                  .split(',')
                  .map((id) => id.trim())
                  .filter(Boolean)
              ),
            };
          })
          .filter(Boolean);
      }

      updateCount(value) {
        if (!this.count) return;
        const itemCount = Number(value) || 0;
        this.count.hidden = itemCount === 0;
        this.count.textContent = itemCount > 99 ? '99+' : String(itemCount);
      }

      scrollToCheckoutFromHash() {
        if (window.location.hash !== '#checkout') return;

        const scrollToTarget = () => {
          const checkout = document.getElementById('checkout');
          if (!checkout) return;
          const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          checkout.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
          if (typeof checkout.focus === 'function') checkout.focus({ preventScroll: true });
        };

        window.setTimeout(scrollToTarget, 350);
        window.addEventListener('load', () => window.setTimeout(scrollToTarget, 180), { once: true });
      }
    }
  );
}
